const DEFAULT_LANG = "en-GB";
const VOICE_RATE = 0.98;
const VOICE_PITCH = 1;
const VOICE_VOLUME = 1;
const DEFAULT_RECOGNITION_WATCHDOG_MS = 20000;
const recognitionWatchdogs = new WeakMap();
const cancelledRecognitions = new WeakSet();

export const VOICE_UI_STATES = Object.freeze({
  IDLE: "idle",
  REQUESTING_PERMISSION: "requesting_permission",
  LISTENING: "listening",
  PROCESSING: "processing",
  SYNTHESIZING: "synthesizing",
  READY_TO_SPEAK: "ready_to_speak",
  SPEAKING: "speaking",
  ERROR: "error",
});
const PREFERRED_VOICE_NAMES = {
  en: ["sonia", "libby", "samantha", "google uk english female"],
  es: ["elvira", "helena", "monica", "google espanol"],
};

let cachedVoices = [];
let observedSynthesis = null;

export function getSpeechRecognitionConstructor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function supportsVoiceInput() {
  return Boolean(getSpeechRecognitionConstructor());
}

function voiceErrorMessage(error) {
  const code = typeof error === "string" ? error : error?.error;
  if (code === "not-allowed" || code === "service-not-allowed") {
    return "Microphone permission was denied. Text chat still works.";
  }
  if (code === "audio-capture") {
    return "No microphone was found. Text chat still works.";
  }
  if (code === "no-speech") {
    return "I did not hear anything. You can try again or type instead.";
  }
  if (code === "aborted") {
    return "Voice input stopped. Text chat still works.";
  }
  if (code === "network") {
    return "Voice recognition had a network problem. Text chat still works.";
  }
  return "Voice input could not start. You can still type to VibeBee.";
}

export function startVoiceInput({
  onStart,
  onResult,
  onError,
  onEnd,
  onNoMatch,
  onTimeout,
  lang = DEFAULT_LANG,
  watchdogMs = DEFAULT_RECOGNITION_WATCHDOG_MS,
} = {}) {
  const Recognition = getSpeechRecognitionConstructor();
  if (!Recognition) {
    onError?.("Voice input is not supported on this browser. Text chat still works.");
    return null;
  }

  const recognition = new Recognition();
  let terminal = false;
  const clearWatchdog = () => {
    const timer = recognitionWatchdogs.get(recognition);
    if (timer) window.clearTimeout(timer);
    recognitionWatchdogs.delete(recognition);
  };
  recognition.lang = lang;
  recognition.continuous = false;
  recognition.interimResults = false;
  recognition.maxAlternatives = 1;

  recognition.onstart = () => onStart?.();
  recognition.onnomatch = () => {
    terminal = true;
    clearWatchdog();
    onNoMatch?.("I could not understand that. You can try again or type instead.");
  };
  recognition.onerror = (event) => {
    if (cancelledRecognitions.has(recognition)) {
      terminal = true;
      clearWatchdog();
      return;
    }
    if (terminal) return;
    terminal = true;
    clearWatchdog();
    onError?.(voiceErrorMessage(event));
  };
  recognition.onend = () => {
    clearWatchdog();
    cancelledRecognitions.delete(recognition);
    onEnd?.();
  };
  recognition.onresult = (event) => {
    terminal = true;
    clearWatchdog();
    const transcript = Array.from(event.results || [])
      .map((result) => result?.[0]?.transcript || "")
      .join(" ")
      .trim();
    if (transcript) {
      onResult?.(transcript);
    } else {
      onNoMatch?.("I did not catch that. You can try again or type instead.");
    }
  };

  try {
    recognition.start();
    if (Number.isFinite(watchdogMs) && watchdogMs > 0) {
      recognitionWatchdogs.set(recognition, window.setTimeout(() => {
        if (terminal) return;
        terminal = true;
        recognitionWatchdogs.delete(recognition);
        recognition.abort?.();
        const message = "Voice isn't available right now — you can still type to VibeBee.";
        onTimeout?.(message);
        if (!onTimeout) onError?.(message);
      }, watchdogMs));
    }
  } catch (error) {
    terminal = true;
    clearWatchdog();
    onError?.(voiceErrorMessage(error));
    return null;
  }

  return recognition;
}

export function cancelVoiceInput(recognition) {
  if (!recognition) return;
  const timer = recognitionWatchdogs.get(recognition);
  if (timer && typeof window !== "undefined") window.clearTimeout(timer);
  recognitionWatchdogs.delete(recognition);
  cancelledRecognitions.add(recognition);
  recognition.abort?.();
}

export function supportsSpeechSynthesis() {
  return typeof window !== "undefined" && Boolean(window.speechSynthesis && window.SpeechSynthesisUtterance);
}

function languageFamily(lang) {
  return String(lang || DEFAULT_LANG).toLowerCase().startsWith("es") ? "es" : "en";
}

export function selectVibeBeeVoice(voices, lang = DEFAULT_LANG) {
  const available = Array.isArray(voices) ? voices.filter(Boolean) : [];
  if (!available.length) return null;
  const family = languageFamily(lang);
  const exact = family === "es" ? "es-es" : "en-gb";
  const preferred = PREFERRED_VOICE_NAMES[family];
  const scored = available
    .map((voice, index) => {
      const voiceLang = String(voice.lang || "").toLowerCase();
      const name = String(voice.name || "").toLowerCase();
      let score = voiceLang === exact ? 100 : voiceLang.startsWith(`${family}-`) || voiceLang === family ? 60 : 0;
      if (!score) return null;
      if (voice.localService === true) score += 20;
      if (/natural|enhanced|premium/.test(name)) score += 15;
      if (preferred.some((candidate) => name.includes(candidate))) score += 10;
      if (voice.default === true) score += 5;
      return { voice, score, index };
    })
    .filter(Boolean)
    .sort((left, right) => right.score - left.score || left.index - right.index);
  return scored[0]?.voice || null;
}

export function refreshVibeBeeVoices() {
  if (!supportsSpeechSynthesis()) {
    cachedVoices = [];
    return cachedVoices;
  }
  cachedVoices = window.speechSynthesis.getVoices?.() || [];
  return cachedVoices;
}

export function prepareVibeBeeVoices() {
  if (!supportsSpeechSynthesis()) return false;
  const synthesis = window.speechSynthesis;
  refreshVibeBeeVoices();
  if (observedSynthesis === synthesis) return true;
  observedSynthesis = synthesis;
  if (typeof synthesis.addEventListener === "function") {
    synthesis.addEventListener("voiceschanged", refreshVibeBeeVoices);
  } else {
    synthesis.onvoiceschanged = refreshVibeBeeVoices;
  }
  return true;
}

export function speechSafeText(text) {
  return String(text || "")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/https?:\/\/\S+/gi, "")
    .replace(/^\s*(?:View Venue|Take Me There)\s*:?.*$/gim, "")
    .replace(/[🍽️🍹🎵🐝✨📍🚕]/gu, "")
    .replace(/\s*[→]\s*/g, ", then ")
    .replace(/\\([*_`#-])/g, "$1")
    .replace(/[*_`#]/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

export function stopVibeBeeSpeech() {
  if (supportsSpeechSynthesis()) {
    window.speechSynthesis.cancel();
  }
}

export function speakVibeBeeAnswer(text, { muted = false, onStart, onEnd, onError, lang = DEFAULT_LANG } = {}) {
  const clean = speechSafeText(text);
  if (muted || !clean) return false;
  if (!supportsSpeechSynthesis()) {
    onError?.("Spoken answers are not supported on this browser. The written answer is still available.");
    return false;
  }

  stopVibeBeeSpeech();
  prepareVibeBeeVoices();
  const utterance = new window.SpeechSynthesisUtterance(clean);
  const voice = selectVibeBeeVoice(cachedVoices, lang);
  if (voice) utterance.voice = voice;
  utterance.lang = voice?.lang || lang;
  utterance.rate = VOICE_RATE;
  utterance.pitch = VOICE_PITCH;
  utterance.volume = VOICE_VOLUME;
  utterance.onstart = () => onStart?.();
  utterance.onend = () => onEnd?.();
  utterance.onerror = () => {
    onError?.("VibeBee could not speak the answer aloud. The written answer is still available.");
    onEnd?.();
  };
  window.speechSynthesis.speak(utterance);
  return true;
}

export function createQuartzPlaybackController({
  requestAudio,
  reportEvent = () => {},
  AudioCtor = typeof Audio === "undefined" ? null : Audio,
  urlApi = typeof URL === "undefined" ? null : URL,
} = {}) {
  let audio = null;
  let responseId = "";
  let generation = 0;
  let playlist = [];
  let cachedAudio = [];
  let pendingAudio = new Map();
  let playbackCallbacks = {};
  let index = 0;
  let startedPlayback = false;
  let synthesisErrors = new Map();
  let playbackTimer = null;

  const emit = (event, id, outcome, started = 0, failureClass = "") => {
    const latency = started ? Math.max(0, Date.now() - started) : 0;
    Promise.resolve(failureClass ? reportEvent(event, id, outcome, latency, failureClass) : reportEvent(event, id, outcome, latency)).catch(() => {});
  };

  const releaseAudio = () => {
    if (audio) {
      audio.pause?.();
      audio.onplay = null;
      audio.onended = null;
      audio.onerror = null;
      audio = null;
    }
    for (const item of cachedAudio) {
      if (item?.url) urlApi?.revokeObjectURL?.(item.url);
    }
    cachedAudio = [];
    synthesisErrors.clear();
    responseId = "";
  };

  const abortPending = () => {
    clearTimeout(playbackTimer);
    for (const item of pendingAudio.values()) item.controller.abort();
    pendingAudio.clear();
  };

  const loadAudio = (partIndex, currentGeneration) => {
    if (synthesisErrors.has(partIndex)) return Promise.reject(synthesisErrors.get(partIndex));
    if (cachedAudio[partIndex]) return Promise.resolve(cachedAudio[partIndex]);
    if (pendingAudio.has(partIndex)) return pendingAudio.get(partIndex).promise;
    const controller = new AbortController();
    let timer;
    const promise = Promise.race([
      requestAudio(playlist[partIndex], { signal: controller.signal }),
      new Promise((_, reject) => controller.signal.addEventListener("abort", () => reject(Object.assign(new Error("aborted"), { name: "AbortError" })), { once: true })),
      new Promise((_, reject) => { timer = setTimeout(() => { reject(new Error("tts_timeout")); controller.abort(); }, 15000); }),
    ]).then((blob) => {
      if (currentGeneration !== generation) throw Object.assign(new Error("stale"), { name: "AbortError" });
      if (!blob || blob.size < 44 || !String(blob.type || "").toLowerCase().startsWith("audio/wav")) throw new Error("invalid_audio");
      const url = urlApi.createObjectURL(blob);
      const player = new AudioCtor(url);
      player.preload = "auto";
      cachedAudio[partIndex] = { player, url };
      return cachedAudio[partIndex];
    }).catch((error) => {
      if (currentGeneration === generation && !["AbortError", "CanceledError"].includes(error?.name)) synthesisErrors.set(partIndex, error);
      throw error;
    }).finally(() => {
      clearTimeout(timer);
      if (pendingAudio.get(partIndex)?.controller === controller) pendingAudio.delete(partIndex);
    });
    pendingAudio.set(partIndex, { controller, promise });
    return promise;
  };

  const failPlayback = (callbacks, currentGeneration, failureClass = "playback_error") => {
    if (currentGeneration !== generation) return;
    generation += 1;
    abortPending();
    audio?.pause?.();
    emit("voice_client_failure", responseId, "failed", 0, failureClass);
    callbacks.onError?.("VibeBee's voice isn't available right now. The written answer is still here.");
  };

  const playCurrent = async ({ replay = false, onStart, onEnd, onError, onAutoplayBlocked } = {}) => {
    if (!audio || !responseId) return { status: "unavailable" };
    const id = responseId;
    const currentGeneration = generation;
    const currentIndex = index;
    if (replay) audio.currentTime = 0;
    audio.onplay = () => {
      if (currentGeneration !== generation || currentIndex !== index) return;
      emit(replay ? "audio_replay" : "audio_play_started", id, "success");
      if (!startedPlayback) { startedPlayback = true; onStart?.(); }
      clearTimeout(playbackTimer);
      playbackTimer = setTimeout(() => failPlayback({ onError }, currentGeneration), 60000);
    };
    audio.onended = () => {
      if (currentGeneration !== generation || currentIndex !== index) return;
      clearTimeout(playbackTimer);
      audio.onended = null;
      if (index + 1 < playlist.length) {
        index += 1;
        void startPart(playbackCallbacks, currentGeneration);
        return;
      }
      emit("audio_play_completed", id, "success");
      onEnd?.();
    };
    audio.onerror = () => {
      if (currentGeneration !== generation) return;
      failPlayback({ onError }, currentGeneration);
    };
    let startupTimer;
    try {
      await Promise.race([
        audio.play(),
        new Promise((_, reject) => { startupTimer = setTimeout(() => reject(new Error("audio_start_timeout")), 15000); }),
      ]);
      if (currentGeneration !== generation) return { status: "stale" };
      // Only one segment ahead, only after playback has been accepted.
      if (index + 1 < playlist.length) void loadAudio(index + 1, currentGeneration).catch(() => {});
      return { status: "playing", responseId: id };
    } catch (error) {
      if (currentGeneration !== generation) return { status: "stale" };
      if (error?.name !== "NotAllowedError") {
        failPlayback({ onError }, currentGeneration);
        return { status: "failed", responseId: id };
      }
      emit("autoplay_blocked", id, "blocked");
      onAutoplayBlocked?.(error);
      return { status: "blocked", responseId: id };
    } finally {
      clearTimeout(startupTimer);
    }
  };

  const startPart = async (callbacks, currentGeneration) => {
    try {
      const item = await loadAudio(index, currentGeneration);
      if (currentGeneration !== generation) return { status: "stale" };
      audio = item.player;
      return await playCurrent(callbacks);
    } catch (error) {
      if (currentGeneration !== generation) return { status: "stale" };
      if (error?.name === "AbortError" || error?.name === "CanceledError") return { status: "aborted" };
      failPlayback(callbacks, currentGeneration, error?.message === "invalid_audio" ? "tts_unusable_audio" : "tts_request_failed");
      return { status: "failed" };
    }
  };

  return {
    async synthesizeAndPlay(presentation, callbacks = {}) {
      const token = presentation?.synthesis_token;
      if (!token || !requestAudio || !AudioCtor || !urlApi?.createObjectURL) {
        if (token && (!AudioCtor || !urlApi?.createObjectURL)) emit("voice_client_failure", presentation.response_id, "failed", 0, "unsupported_browser");
        return { status: "unavailable" };
      }
      generation += 1;
      const currentGeneration = generation;
      abortPending();
      releaseAudio();
      playlist = Array.isArray(presentation.segments) && presentation.segments.length > 1 && presentation.segments.length <= 3
        ? presentation.segments.map((segment) => ({ ...presentation, ...segment, segments: [] }))
        : [presentation];
      responseId = presentation.response_id;
      index = 0;
      startedPlayback = false;
      playbackCallbacks = callbacks;
      callbacks.onSynthesizing?.();
      const result = await startPart(callbacks, currentGeneration);
      if (result.status === "aborted") emit("tts_aborted", presentation.response_id, "aborted");
      return result;
    },
    play(callbacks = {}) {
      startedPlayback = false;
      playbackCallbacks = callbacks;
      return playCurrent(callbacks);
    },
    replay(callbacks = {}) {
      if (!cachedAudio.length) return Promise.resolve({ status: "unavailable" });
      generation += 1;
      abortPending();
      audio?.pause?.();
      index = 0;
      startedPlayback = false;
      playbackCallbacks = { ...callbacks, replay: true };
      return startPart(playbackCallbacks, generation);
    },
    stop({ release = false } = {}) {
      const wasPending = pendingAudio.size > 0;
      generation += 1;
      abortPending();
      startedPlayback = false;
      if (audio) {
        audio.pause?.();
        audio.currentTime = 0;
        emit("audio_play_stopped", responseId, "stopped");
      } else if (wasPending) emit("tts_aborted", responseId, "aborted");
      if (release) releaseAudio();
    },
    hasReplay() {
      return Boolean(audio && responseId);
    },
    currentResponseId() {
      return responseId;
    },
    dispose() {
      generation += 1;
      abortPending();
      releaseAudio();
    },
  };
}
